/**
 * Federation Rules Penalty Engine
 * Auto-calculates penalties and points based on CMAS/AIDA rules
 * Uses the parsed rulesets from /data/cmasRules.js and /data/aidaRules.js
 */
import { CMAS_RULES } from '../data/cmasRules';
import { AIDA_RULES } from '../data/aidaRules';

/**
 * Get discipline info for a given event code + federation
 */
const getDiscipline = (eventCode, federation) => {
  const rules = federation === 'CMAS' ? CMAS_RULES : AIDA_RULES;
  const pool = rules.disciplines?.pool || {};
  const depth = rules.disciplines?.depth || {};
  // Normalize code: DYNBF -> DYNB for AIDA
  const code = federation === 'AIDA' && eventCode === 'DYNBF' ? 'DYNB' : eventCode;
  return pool[code] || depth[code] || null;
};

/**
 * Get measurement unit for event type
 */
export const getMeasurementUnit = (eventCode, federation) => {
  const disc = getDiscipline(eventCode, federation);
  return disc?.measurementUnit || 'distance';
};

/**
 * Calculate base points from realized performance
 */
export const calculateBasePoints = (realized, eventCode, federation) => {
  if (!realized || realized <= 0) return 0;
  const disc = getDiscipline(eventCode, federation);
  if (!disc) return 0;

  if (federation === 'AIDA') {
    const ppu = disc.pointsPerUnit || 0.5;
    return Math.floor(realized * ppu * 10) / 10; // round down to 1 decimal
  }
  // CMAS: pool disciplines use distance/time directly, depth uses meters
  if (disc.measurementUnit === 'time') {
    return Math.floor(realized); // round down to full seconds
  }
  if (disc.measurementUnit === 'distance') {
    return Math.floor(realized * 2) / 2; // round down to 0.5m
  }
  return realized; // depth: exact meters
};

/**
 * Calculate underperformance penalty (RP < AP)
 */
export const calculateUnderPerformancePenalty = (announced, realized, eventCode, federation) => {
  if (!announced || !realized || realized >= announced) return { points: 0, gap: 0, description: null };
  const gap = announced - realized;
  const disc = getDiscipline(eventCode, federation);

  if (federation === 'AIDA') {
    // AIDA: deduction = gap * pointsPerUnit
    const ppu = disc?.pointsPerUnit || 0.5;
    const points = gap * ppu;
    const unit = disc?.measurementUnit === 'time' ? 's' : 'm';
    return {
      points: Math.round(points * 10) / 10,
      gap: Math.round(gap * 10) / 10,
      description: `Under AP: -${gap}${unit} x ${ppu} = -${(points).toFixed(1)} pts`
    };
  }
  // CMAS: distance events subtract meters, time events subtract seconds
  if (disc?.measurementUnit === 'distance' || disc?.measurementUnit === 'depth') {
    return { points: gap, gap, description: `Under AP: -${gap}m penalty` };
  }
  return { points: gap, gap, description: `Under AP: -${gap}s penalty` };
};

/**
 * Calculate late start penalty
 */
export const calculateLateStartPenalty = (secondsLate, federation) => {
  if (!secondsLate || secondsLate <= 0) return { points: 0, isDQ: false, description: null };

  if (federation === 'AIDA') {
    // AIDA: 10s window no penalty, then 1pt per 5s, DQ after 30s
    if (secondsLate <= 10) return { points: 0, isDQ: false, description: null };
    if (secondsLate > 30) return { points: 0, isDQ: true, description: 'Late Start > 30s: DQ' };
    const excess = secondsLate - 10;
    const blocks = Math.ceil(excess / 5);
    return { points: blocks, isDQ: false, description: `Late Start: ${secondsLate}s (-${blocks} pts)` };
  }
  // CMAS: DQ after 30s, no graduated penalty
  if (secondsLate > 30) return { points: 0, isDQ: true, description: 'Late Start > 30s: DQ' };
  return { points: 0, isDQ: false, description: null };
};

/**
 * Calculate early start penalty
 */
export const calculateEarlyStartPenalty = (secondsEarly, federation) => {
  if (!secondsEarly || secondsEarly <= 0) return { points: 0, isDQ: false, description: null };

  if (federation === 'AIDA') {
    const blocks = Math.ceil(secondsEarly / 5);
    return { points: blocks, isDQ: false, description: `Early Start: -${secondsEarly}s (-${blocks} pts)` };
  }
  // CMAS: early start = DQ
  return { points: 0, isDQ: true, description: 'Early Start: DQ (CMAS)' };
};

/**
 * All available penalty types for a federation
 */
export const getPenaltyTypes = (federation, eventCode) => {
  const disc = getDiscipline(eventCode, federation);
  const isPool = disc?.type === 'pool';
  const isDepth = disc?.type === 'depth';

  const types = [
    { code: 'LATE', label: 'Late Start', hasInput: true, inputType: 'seconds', group: 'timing' },
    { code: 'EARLY', label: 'Early Start', hasInput: true, inputType: 'seconds', group: 'timing' },
    { code: 'SHORT', label: 'Under AP (RP < AP)', auto: true, group: 'performance' },
  ];

  if (federation === 'AIDA') {
    if (isPool) {
      types.push(
        { code: 'START_TURN', label: 'Start/Turn Violation', hasInput: true, inputType: 'count', points: 5, group: 'pool' },
        { code: 'PULL_POOL', label: 'Pull on Support', hasInput: true, inputType: 'count', points: 5, group: 'pool' },
      );
    }
    if (isDepth) {
      types.push(
        { code: 'GRAB', label: 'Grab Line', hasInput: true, inputType: 'count', points: 5, group: 'depth' },
        { code: 'TAG', label: 'Tag Missing', hasInput: false, points: 1, group: 'depth' },
        { code: 'LANYARD', label: 'Lanyard Removed', hasInput: false, points: 10, group: 'depth' },
      );
    }
  }
  if (federation === 'CMAS') {
    if (isPool) {
      types.push(
        { code: 'DOLPHIN_KICK', label: 'Dolphin Kick Violation', hasInput: true, inputType: 'count', points: 1, unit: 'm', group: 'pool' },
        { code: 'WALL_TOUCH', label: 'Wall Touch Missing', hasInput: false, points: 3, unit: 'm', group: 'pool' },
      );
    }
    if (isDepth) {
      types.push(
        { code: 'TAG_MISSING', label: 'Tag Missing', hasInput: false, points: 1, unit: 'm', group: 'depth' },
      );
    }
  }

  // DQ reasons for both
  types.push(
    { code: 'DQ_SP', label: 'Surface Protocol Failure', isDQ: true, group: 'dq' },
    { code: 'DQ_BO', label: 'Blackout', isDQ: true, group: 'dq' },
    { code: 'DQ_AIRWAY', label: 'Airway Submersion', isDQ: true, group: 'dq' },
    { code: 'DQ_OTHER', label: 'Other DQ', isDQ: true, group: 'dq' },
  );

  return types;
};

/**
 * Surface Protocol info
 */
export const getSurfaceProtocolInfo = (federation) => {
  if (federation === 'AIDA') {
    return {
      timeLimit: AIDA_RULES.timing.surfaceProtocol.timeLimit,
      tasks: AIDA_RULES.timing.surfaceProtocol.tasks,
      order: AIDA_RULES.timing.surfaceProtocol.order
    };
  }
  return {
    timeLimit: CMAS_RULES.timing.surfaceProtocol.timeLimit,
    tasks: CMAS_RULES.timing.surfaceProtocol.requirements
  };
};

/**
 * Full penalty calculation for a passage
 * @param {Object} params - { announced, realized, eventCode, federation, penalties: [{code, value}] }
 * @returns {Object} { basePoints, totalPenaltyPoints, finalScore, isDQ, breakdown[] }
 */
export const calculateFullScore = ({ announced, realized, eventCode, federation, penalties = [] }) => {
  const breakdown = [];
  let totalPenalty = 0;
  let isDQ = false;

  // Base points
  const basePoints = calculateBasePoints(realized, eventCode, federation);
  breakdown.push({ label: 'Base Points', points: basePoints, type: 'base' });

  // Under AP penalty (automatic)
  const underAP = calculateUnderPerformancePenalty(announced, realized, eventCode, federation);
  if (underAP.points > 0) {
    totalPenalty += underAP.points;
    breakdown.push({ label: underAP.description, points: -underAP.points, type: 'penalty', code: 'SHORT' });
  }

  // Additional penalties
  for (const p of penalties) {
    if (p.code === 'LATE') {
      const late = calculateLateStartPenalty(p.value, federation);
      if (late.isDQ) { isDQ = true; breakdown.push({ label: late.description, points: 0, type: 'dq', code: 'LATE' }); }
      else if (late.points > 0) { totalPenalty += late.points; breakdown.push({ label: late.description, points: -late.points, type: 'penalty', code: 'LATE' }); }
    } else if (p.code === 'EARLY') {
      const early = calculateEarlyStartPenalty(p.value, federation);
      if (early.isDQ) { isDQ = true; breakdown.push({ label: early.description, points: 0, type: 'dq', code: 'EARLY' }); }
      else if (early.points > 0) { totalPenalty += early.points; breakdown.push({ label: early.description, points: -early.points, type: 'penalty', code: 'EARLY' }); }
    } else if (p.code === 'START_TURN' || p.code === 'PULL_POOL' || p.code === 'GRAB') {
      const count = p.value || 1;
      const ptsEach = p.code === 'START_TURN' ? 5 : p.code === 'PULL_POOL' ? 5 : 5;
      const pts = count * ptsEach;
      totalPenalty += pts;
      breakdown.push({ label: `${p.code}: ${count}x (-${pts} pts)`, points: -pts, type: 'penalty', code: p.code });
    } else if (p.code === 'TAG') {
      totalPenalty += 1;
      breakdown.push({ label: 'Tag Missing: -1 pt', points: -1, type: 'penalty', code: 'TAG' });
    } else if (p.code === 'LANYARD') {
      totalPenalty += 10;
      breakdown.push({ label: 'Lanyard Removed: -10 pts', points: -10, type: 'penalty', code: 'LANYARD' });
    } else if (p.code === 'DOLPHIN_KICK') {
      const count = p.value || 1;
      totalPenalty += count;
      breakdown.push({ label: `Dolphin Kick: ${count}x (-${count}m)`, points: -count, type: 'penalty', code: 'DOLPHIN_KICK' });
    } else if (p.code === 'WALL_TOUCH') {
      totalPenalty += 3;
      breakdown.push({ label: 'Wall Touch: -3m', points: -3, type: 'penalty', code: 'WALL_TOUCH' });
    } else if (p.code === 'TAG_MISSING') {
      totalPenalty += 1;
      breakdown.push({ label: 'Tag Missing: -1m', points: -1, type: 'penalty', code: 'TAG_MISSING' });
    } else if (p.code?.startsWith('DQ_')) {
      isDQ = true;
      breakdown.push({ label: `DQ: ${p.label || p.code}`, points: 0, type: 'dq', code: p.code });
    }
  }

  const finalScore = isDQ ? 0 : Math.max(0, Math.round((basePoints - totalPenalty) * 10) / 10);

  return { basePoints, totalPenaltyPoints: totalPenalty, finalScore, isDQ, breakdown };
};

/**
 * Get card color based on score result
 */
export const determineCard = (result) => {
  if (result.isDQ) return 'red';
  if (result.totalPenaltyPoints > 0) return 'yellow';
  return 'white';
};

/**
 * Format points display  
 */
export const formatPoints = (points, federation) => {
  if (federation === 'AIDA') return `${points.toFixed(1)} pts`;
  return `${points}`;
};
